// fw JobCard — the mock's .jcard: a time column, a title, a meta line and an
// optional trailing slot. When onClick is given the WHOLE card is a <button>
// at least 48px tall — one thumb, no hunting for the tappable part.
//
// ── `badges` and `facts`, added 2026-09-06 (docs/APP_REBUILD.md §3.3) ────────
//
// The owner's words about the crew screen were "all jobs on the tab show
// nothing" and "can't distinguish active/todo/archived jobs". Measured in
// Chromium against the built app, both were true of this component:
//
//   - a job card carried NO status. Field passed tone='stop' or 'sched' and
//     nothing else, so a colour on a 4px left border was the entire signal —
//     colour alone, which is not a signal at all for a red-green crew member
//     in sunlight.
//   - everything that was not the title arrived pre-joined into one grey
//     string (`Today.js jobMeta()`), so "Whitfield Residence · Front and back.
//     Edge the walk. · 2 hrs est." wrapped to five lines of the same weight
//     and none of it could be read at a glance.
//
// So the card now has the four slots a person actually reads, in the order
// they read them, and each one knows what it is:
//
//   title    what the job is
//   badges   what STATE it is in — colour AND the word, never colour alone
//   meta     where it is (the property or the client)
//   facts    the two or three short numbers: work-list progress, the estimate
//
// `badges` takes the shape apps/shared/jobFields.js `jobBadges()` returns —
// `{ key, label, tone }` — deliberately, so Field, Office and Dispatch cannot
// grow three vocabularies for the same five statuses. Both props are optional
// and default to empty, so every existing call site renders exactly as before.

import React from 'react';
import { Pill } from './Pill.js';
import './JobCard.css';

export const JOBCARD_TONES = ['neutral', 'now', 'ok', 'sched', 'stop', 'warn'];

const list = (v) => (Array.isArray(v) ? v.filter((x) => x !== null && x !== undefined && x !== false && x !== '') : []);

export function JobCard({ time, title, meta, facts, badges, tone = 'neutral', onClick, onTitleClick, titleLabel, trailing, className = '', ...rest }) {
  const t = JOBCARD_TONES.includes(tone) ? tone : 'neutral';
  const factList = list(facts);
  const badgeList = list(badges);
  // ── A TITLE THAT OPENS THE RECORD ────────────────────────────────────────
  //
  // Owner, 2026-09-18: "WTF did I say about the jobs having a clickable link
  // to open a modal with details to review inside the timesheet??!!! what the
  // fuck is that stupid fucking 'what is <job>' Looks fucking stupid and
  // cheap".
  //
  // The timesheet was rendering a full-width ghost button UNDER every line
  // reading "What is Grid mow (ui-check)?" — measured: five of them on a
  // five-line sheet, each wrapping to two lines at 390px. It existed because
  // `trailing` renders INSIDE this component's <button> when the card is
  // tappable, and a button inside a button is invalid and does not fire, so
  // the affordance had been pushed out of the card and into a row of its own.
  //
  // `onTitleClick` removes the cause instead of restyling the symptom: when it
  // is given, the card is NEVER a <button>, the title is, and `trailing` is
  // then free to hold a real control. So a line can say "open this job" on its
  // name and "edit these hours" on a pencil, which is what was asked for.
  //
  // `onClick` with `onTitleClick` would be two nested click targets with no way
  // to tell which you hit, so it is ignored, loudly, rather than silently: the
  // caller moves that action into `trailing`.
  const titleOpens = typeof onTitleClick === 'function';
  const wholeCardTaps = !titleOpens && typeof onClick === 'function';
  const cls = ['fw-jcard', `fw-jcard--${t}`, wholeCardTaps ? 'fw-jcard--tappable' : '',
    titleOpens ? 'fw-jcard--titlelink' : '', className].filter(Boolean).join(' ');
  const inner = (
    <>
      <span className="fw-jcard__time fw-num">{time}</span>
      <span className="fw-jcard__text">
        {titleOpens ? (
          <button
            type="button"
            className="fw-jcard__title fw-jcard__titlebtn"
            onClick={onTitleClick}
            aria-label={titleLabel}
            title={titleLabel}
          >
            {title}
          </button>
        ) : (
          <span className="fw-jcard__title">{title}</span>
        )}
        {badgeList.length > 0 ? (
          <span className="fw-jcard__badges">
            {badgeList.map((b, i) => <Pill key={b.key || i} tone={b.tone}>{b.label}</Pill>)}
          </span>
        ) : null}
        {meta ? <span className="fw-jcard__meta">{meta}</span> : null}
        {factList.length > 0 ? (
          <span className="fw-jcard__facts">
            {factList.map((f, i) => <span key={i} className="fw-jcard__fact">{f}</span>)}
          </span>
        ) : null}
      </span>
      {trailing !== undefined && trailing !== null ? <span className="fw-jcard__trailing">{trailing}</span> : null}
    </>
  );
  if (wholeCardTaps) {
    return (
      <button type="button" className={cls} data-tone={t} onClick={onClick} {...rest}>
        {inner}
      </button>
    );
  }
  return (
    <div className={cls} data-tone={t} {...rest}>
      {inner}
    </div>
  );
}

export default JobCard;
